// Root route — handles the auth gate, mounts the ledger chrome (desktop
// header / mobile tab bar + sheet), and renders the matched child route. The
// QueryClientProvider lives in `main.tsx`; here we only consume the auth
// state and decide between the auth screen and the app.

import { Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAuth, getApiUrl } from '../lib/auth';
import { apiClient, ApiError, ApiNetworkError, formatApiError } from '../lib/api';
import { WhoamiResponseSchema, type WhoamiResponse } from '../lib/types';
import { C, FONT_DISPLAY, FONT_MONO } from '../lib/design';
import { useIsDesktop } from '../lib/useMediaQuery';
import { LedgerProvider, storePage } from '../lib/ledger-state';
import { Header } from '../components/ledger/Header';
import { MobileShell } from '../components/ledger/MobileShell';

function AuthGate() {
  const [token, setTokenInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { signIn } = useAuth();
  // Resolved at module-import time. Baked at build via VITE_PROTOCOL_API_URL;
  // shown below the form for transparency so the operator knows where their
  // token is going. Not editable — V1.1 dropped the field.
  const resolvedApiUrl = getApiUrl();

  async function onConnect(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = token.trim();
    if (!trimmed) {
      setError('Paste a token before connecting.');
      return;
    }
    setBusy(true);
    // Stash the candidate token in localStorage so apiClient picks it up on
    // the verification request. If verification fails, we clear it.
    try {
      localStorage.setItem('protocol-api-token', trimmed);
    } catch {
      // Storage failed (private mode etc.). Fall through; the request will
      // surface its own failure mode.
    }
    try {
      await apiClient.get<WhoamiResponse>('/api/whoami', { schema: WhoamiResponseSchema });
      signIn(trimmed); // also notifies subscribers so the shell re-renders.
    } catch (err) {
      // Clear the candidate token — it failed verification.
      try {
        localStorage.removeItem('protocol-api-token');
      } catch {
        // ignore
      }
      if (err instanceof ApiError && err.kind === 'auth') {
        setError("That token doesn't match. Generate a new one or check 1Password.");
      } else if (err instanceof ApiNetworkError) {
        setError(err.message);
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{ background: C.bg, color: C.text, fontFamily: FONT_MONO, minHeight: '100vh' }}
      className="flex items-center justify-center px-5"
    >
      <form onSubmit={onConnect} style={{ width: '100%', maxWidth: 480 }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 32, letterSpacing: '-0.02em', lineHeight: 1, marginBottom: 8 }}>
          <span style={{ fontWeight: 300, fontStyle: 'italic' }}>the</span> protocol
        </div>
        <div style={{ color: C.faint, fontSize: 10, letterSpacing: '0.15em', marginBottom: 32 }}>
          AUTHENTICATE
        </div>

        <label
          htmlFor="protocol-token"
          style={{ display: 'block', color: C.dim, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}
        >
          Paste your Protocol API token
        </label>
        <input
          id="protocol-token"
          type="password"
          value={token}
          onChange={(e) => setTokenInput(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          autoFocus
          disabled={busy}
          style={{
            width: '100%',
            background: C.panel,
            color: C.text,
            border: `1px solid ${C.line}`,
            borderRadius: 0,
            padding: '12px 14px',
            fontFamily: FONT_MONO,
            fontSize: 13,
            letterSpacing: '0.05em',
            outline: 'none',
          }}
        />

        <div style={{ marginTop: 10, color: C.faint, fontSize: 11, lineHeight: 1.5 }}>
          This token was generated by <code style={{ color: C.dim }}>scripts/gen-token.js</code> during initial deploy. Stored in 1Password under "Protocol API." If you don't have one yet, see <code style={{ color: C.dim }}>apps/protocol-api/README.md</code>.
        </div>

        <div
          style={{
            marginTop: 16,
            color: C.faint,
            fontSize: 10,
            letterSpacing: '0.15em',
            lineHeight: 1.5,
          }}
        >
          CONNECTING TO:{' '}
          <code style={{ color: C.dim }}>{resolvedApiUrl || '(not configured)'}</code>
        </div>

        {error && (
          <div
            style={{
              marginTop: 16,
              padding: '10px 14px',
              border: `1px dashed ${C.red}`,
              color: C.red,
              fontSize: 11,
              letterSpacing: '0.05em',
              whiteSpace: 'pre-wrap',
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          style={{
            marginTop: 24,
            width: '100%',
            background: C.signal,
            color: C.bg,
            border: 0,
            borderRadius: 0,
            padding: '14px 18px',
            fontFamily: FONT_MONO,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.2em',
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'VERIFYING…' : 'CONNECT'}
        </button>
      </form>
    </div>
  );
}

function AppShell() {
  const currentPath = useRouterState().location.pathname;
  const isDesktop = useIsDesktop();

  // Remember the last ledger page so `/` reopens it next launch.
  useEffect(() => storePage(currentPath), [currentPath]);

  if (isDesktop) {
    return (
      <div style={{ background: C.bg, color: C.text, fontFamily: FONT_MONO, minHeight: '100vh' }}>
        <Header />
        <Outlet />
      </div>
    );
  }
  return (
    <MobileShell>
      <Outlet />
    </MobileShell>
  );
}

function RootComponent() {
  const { isAuthed } = useAuth();
  if (!isAuthed) return <AuthGate />;
  return (
    <LedgerProvider>
      <AppShell />
    </LedgerProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
